import React from "react";
import { useTranslation } from "react-i18next";
import { convertPrice } from "../../../../services/currencyService";
import { isAdminUser } from "../../../../services/authService";

import "./PackageTable.scss";
import { ApiReqBaseUrl } from "../../../../assets/GlobalVariables";
import { Button, Dropdown, message, Tooltip } from "antd";
import {
  CheckCircleTwoTone,
  CloseCircleTwoTone,
  GiftOutlined,
  ToolOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";

// Import FontAwesome icons for luggage and trolley
import { FaSuitcase, FaShoppingBag } from "react-icons/fa";
import dayjs from "dayjs";

// Updated PassengersNamesRenderer for flights.passengersinfo structure
const PassengersNamesRenderer = ({ roomsAndPaxInfo, divKey, supplement }) => {
  // roomsAndPaxInfo is an array of room objects, each with adults/kids/infants arrays
  if (!Array.isArray(roomsAndPaxInfo) || roomsAndPaxInfo.length === 0) {
    return (
      <div className="passenger-name" key={divKey}>
        -
      </div>
    );
  }
  const allPassengers = [];
  roomsAndPaxInfo.forEach((room) => {
    if (Array.isArray(room.adults)) {
      allPassengers.push(...room.adults);
    }
    if (Array.isArray(room.kids)) {
      allPassengers.push(...room.kids);
    }
    if (Array.isArray(room.infants)) {
      allPassengers.push(...room.infants);
    }
  });

  // supplement is an object, keys are supplement ids, values are supplement objects
  // Each supplement may have a pax.email or pax.firstname/lastname to match
  const supplementArr =
    supplement && typeof supplement === "object"
      ? Object.values(supplement)
      : [];

  function getIconsForSupplements(pax) {
    // Return an array of icon+tooltip for each supplement for this pax
    return supplementArr
      .filter((supp) => {
        if (!supp.checked) return false;
        if (supp.pax && typeof supp.pax === "object") {
          if (
            pax["@seqNum"] !== undefined &&
            supp.pax["@seqNum"] !== undefined &&
            supp.pax["@seqNum"] === pax["@seqNum"]
          )
            return true;
        }
        return false;
      })
      .map((supp, i) => {
        let icon = <FaShoppingBag />;
        if (supp.type && typeof supp.type === "string") {
          if (supp.type.toLowerCase().includes("luggage"))
            icon = <FaSuitcase />; // suitcase for luggage
          else if (supp.type.toLowerCase().includes("trolley"))
            icon = <FaShoppingBag />; // shopping bag for trolley
          else if (supp.type.toLowerCase().includes("meal"))
            icon = <GiftOutlined />;
          else if (supp.type.toLowerCase().includes("insurance"))
            icon = <InfoCircleOutlined />;
          else if (supp.type.toLowerCase().includes("equipment"))
            icon = <ToolOutlined />;
        }
        const tooltip = `${supp.type || ""}${
          supp.weightText ? " - " + supp.weightText : ""
        }${supp.description ? " - " + supp.description : ""}`;
        return (
          <Tooltip key={supp.id || i} title={tooltip}>
            <span style={{ marginLeft: 2, color: "#faad14", fontSize: 16 }}>
              {icon}
            </span>
          </Tooltip>
        );
      });
  }

  return (
    <div className="passenger-name" key={divKey}>
      {allPassengers.map((pax, idx) => {
        const suppIcons = getIconsForSupplements(pax);
        return (
          <div
            key={pax.email || idx}
            style={{ display: "flex", alignItems: "center", gap: 4 }}
          >
            {pax.title ? pax.title + " " : ""}
            {pax.firstname ? pax.firstname + " " : ""}
            {pax.lastname || ""}
            {suppIcons.length > 0 && suppIcons}
          </div>
        );
      })}
    </div>
  );
};

const CollapsedPackageRow = ({ row, orders, setOrders } = {}) => {
  const { t } = useTranslation();
  const [isRequesting, setIsRequesting] = React.useState(false);
  const isAdmin = isAdminUser();

  // Use new structure
  const agent = row.agent || {};
  const hotel = row.hotel || {};
  const { handlingfee, handle_fee_option } = hotel;
  const flights = row.flights || {};
  const supplement = row.supplement || {};
  const payments = Array.isArray(row.payments) ? row.payments : [];
  // Calculate sum paid from payments array (sum of all payment.amount where payment.status === 'paid')
  const sumpaid = payments
    .filter((p) => p && p.paid)
    .reduce((acc, p) => acc + (parseFloat(p.amount) || 0), 0);
  const commission = row.commission || 0;
  const paid = row.paid || false;
  const pending = row.pending || false;
  const cancelled = row.cancelled || false;

  const { flight_booked, hotel_booked } = row || {
    flight_booked: false,
    hotel_booked: false,
  };

  // Helper for booking status
  const bookingStatus = (booked) => (
    <span
      style={{
        fontWeight: 600,
        display: "inline-flex",
        alignItems: "center",
        marginLeft: 6,
        gap: 4,
      }}
    >
      {booked ? (
        <>
          <CheckCircleTwoTone twoToneColor="#52c41a" style={{ fontSize: 18 }} />{" "}
          הוזמן
        </>
      ) : (
        <>
          <CloseCircleTwoTone twoToneColor="#ff4d4f" style={{ fontSize: 18 }} />{" "}
          לא הוזמן
        </>
      )}
    </span>
  );
  const supplierPNR = flights.supplierPNR;
  // Use flights.passengersinfo if available, else fallback to row.passengersinfo
  let roomsAndPaxInfo = [];
  if (flights.passengersinfo) {
    roomsAndPaxInfo = flights.passengersinfo;
  } else {
    roomsAndPaxInfo = row.passengersinfo || [];
  }
  // For main contact, use first adult if available
  const mainContact = roomsAndPaxInfo?.[0]?.adults?.[0] || {};
  const hotelName = hotel.hotelname;
  const hotelCheckIn = hotel.checkin || row.check_in;
  const hotelCheckOut = hotel.checkout || row.check_out;
  const hotelRooms = hotel.rooms || [];
  const flightInfo = flights.linkedflights?.flight || [];
  const firstWay = flightInfo[0]?.leg || {};
  const returnWay = flightInfo[1]?.leg || {};
  const supplierId = firstWay.supplierId;
  const supplementsArr = Object.values(supplement).filter((s) => s.checked);
  const totalToPay = row.total_price;

  const changeOrderStatus = async ({ orderId, status }) => {
    try {
      const agentId = JSON.parse(localStorage.getItem("UserInfo")).id;
      setIsRequesting(true);
      const res = await fetch(ApiReqBaseUrl + `/lds_orders/${orderId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ status, agent_id: agentId }),
      });
      if (res.status === 200) {
        message.success("הסטאטוס שונה בהצלחה");
        const order_res = await res.json();
        const newOrders = orders.map((order) =>
          order.id === orderId ? order_res : order
        );
        setOrders(newOrders);
      } else {
        message.error("שגיאה בשינוי סטאטוס");
      }
      setIsRequesting(false);
    } catch (error) {
      console.log("error", error);
      setIsRequesting(false);
    }
  };

  return (
    <div className="collapsed-wrapper">
      <div className="column-wrapper">
        {/* Agent Info */}
        {agent.email && (
          <>
            <div className="item-wrapper">
              <div className="item-header">{t("orders.agentName")}</div>
              <div>
                {agent.firstname} {agent.lastname}
              </div>
            </div>
            <div className="item-wrapper">
              <div className="item-header">{t("orders.agentEmail")}</div>
              <div>{agent.email}</div>
            </div>
            <div className="item-wrapper">
              <div className="item-header">{t("orders.agentPhone")}</div>
              <div>{agent.cell}</div>
            </div>
          </>
        )}
        <div className="item-wrapper">
          <div className="item-header">{t("orders.roomType")}</div>
          <div>{hotelRooms[0]?.roomname || hotelRooms[0]?.roomType || "-"}</div>
        </div>
        {supplementsArr.length > 0 && (
          <div className="item-wrapper">
            <div className="item-header">כבודה</div>
            <ul>
              {supplementsArr.map((supp, i) => (
                <li key={i}>
                  {supp.type} - {supp.description} ({supp.weightText})
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="item-wrapper">
          <div className="item-header">{t("orders.passengersNames")}</div>
          <PassengersNamesRenderer
            roomsAndPaxInfo={roomsAndPaxInfo}
            divKey={row.id}
            supplement={flights.supplement}
          />
        </div>
        <div className="item-wrapper">
          <div className="item-header">איש קשר ראשי</div>
          <div>
            {mainContact.title} {mainContact.firstname} {mainContact.lastname}
          </div>
          <div>{mainContact.email}</div>
          <div>{mainContact.cell}</div>
        </div>
      </div>
      <div className="column-wrapper">
        <div className="item-wrapper">
          <div className="item-header">תאריך הזמנה</div>
          <div>{row.created_at}</div>
        </div>
        <div className="item-wrapper">
          <div className="item-header">טיסה הלוך</div>
          <div>{firstWay.airlineId}</div>
          <div>{firstWay.flightClass + firstWay.flightNumber}</div>
          <div>{firstWay.depDate}</div>
        </div>
        <div className="item-wrapper">
          <div className="item-header">טיסה חזור</div>
          <div>{returnWay.airlineId}</div>
          <div>{returnWay.flightClass + returnWay.flightNumber}</div>
          <div>{returnWay.depDate}</div>
        </div>
        <div className="item-wrapper">
          <div className="item-header">כניסה למלון</div>
          <div>{hotelCheckIn}</div>
        </div>
        <div className="item-wrapper">
          <div className="item-header">יציאה מהמלון</div>
          <div>{hotelCheckOut}</div>
        </div>
        {isAdmin && (
          <div className="item-wrapper">
            <div className="item-header">קוד ספק</div>
            <div>{supplierId}</div>
          </div>
        )}
        {supplierPNR && (
          <div className="item-wrapper">
            <div className="item-header">מספר הזמנה (ספק)</div>
            <div>{supplierPNR}</div>
          </div>
        )}
      </div>
      <div className="column-wrapper">
        <div className="item-wrapper">
          <div className="item-header">{t("orders.toPay")}</div>
          <div>{convertPrice(totalToPay)}</div>
        </div>
        <div className="item-wrapper">
          <div className="item-header">{t("orders.paid")}</div>
          <div>{convertPrice(sumpaid)}</div>
        </div>
        <div className="item-wrapper">
          <div className="item-header">{t("orders.leftToPay")}</div>
          <div>{convertPrice(totalToPay - sumpaid)}</div>
        </div>
        <div className="item-wrapper">
          <div className="item-header">{t("orders.myCommission")}</div>
          <div className="commisions-cell">{convertPrice(commission)}</div>
        </div>
        <div className="item-wrapper">
          <div className="item-header">{t("orders.handlingFee")}</div>
          <div className="commisions-cell">
            {handlingfee ? handlingfee + handle_fee_option : "אין"}
          </div>
        </div>
        {isAdmin && (
          <div className="item-wrapper">
            <Dropdown
              menu={{
                onClick: ({ key }) => {
                  changeOrderStatus({ orderId: row.id, status: key });
                },
                items: [
                  {
                    key: "cancelled",
                    label: cancelled ? "פעילה" : "מבוטלת",
                    disabled: isRequesting,
                  },
                  {
                    key: "paid",
                    label: paid ? "לא שולמה" : "שולמה",
                    disabled: isRequesting,
                  },
                  {
                    key: "pending",
                    label: pending ? `מאושרת ע"י ספק` : "ממתינה לאישור ספק",
                    disabled: isRequesting,
                  },
                ],
              }}
            >
              <Button loading={isRequesting}>שנה סטאטוס</Button>
            </Dropdown>
          </div>
        )}
        {/* Booking status display */}
        <div className="item-wrapper">
          <div className="item-header">סטטוס טיסה</div>
          {bookingStatus(flight_booked)}
        </div>
        <div className="item-wrapper">
          <div className="item-header">סטטוס מלון</div>
          {bookingStatus(hotel_booked)}
        </div>
      </div>
    </div>
  );
};

export default CollapsedPackageRow;
